
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { createErrorHandler, AppError, ValidationError, APIError } from '@/utils/errorHandler';

interface UseErrorHandlerOptions {
  component: string;
  showToast?: boolean;
}

export const useErrorHandler = ({ component, showToast = true }: UseErrorHandlerOptions) => {
  const { toast } = useToast();
  const handleError = createErrorHandler(component);

  const processError = useCallback((error: Error, action: string, additionalInfo?: Record<string, any>) => {
    handleError(error, action, additionalInfo);

    if (!showToast) return;

    let title = 'Error';
    let description = 'An unexpected error occurred';

    if (error instanceof ValidationError) {
      title = 'Validation Error';
      description = error.message;
    } else if (error instanceof APIError) {
      title = 'API Error';
      description = `${error.message} (Status: ${error.statusCode})`;
    } else if (error instanceof AppError) {
      title = 'Application Error';
      description = error.message;
    } else {
      description = error.message || description;
    }

    toast({
      title,
      description,
      variant: 'destructive',
    });
  }, [handleError, showToast, toast]);

  return { handleError: processError };
};
